import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import { SupportChatbot } from "@/components/SupportChatbot";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { NetworkStatus } from "@/components/NetworkStatus";
import { globalQueryErrorHandler, isPermissionError, isAuthError } from "@/lib/apiErrorHandler";
import { Skeleton } from "@/components/ui/skeleton";
import { FEATURES } from "@/lib/featureFlags";

// Lazy-loaded pages
const Index = lazyRetry(() => import("./pages/Index"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Book = lazyRetry(() => import("./pages/Book"));
const StartJob = lazyRetry(() => import("./pages/StartJob"));
const Tracking = lazyRetry(() => import("./pages/Tracking"));
const History = lazyRetry(() => import("./pages/History"));
const Referrals = lazyRetry(() => import("./pages/Referrals"));
const Plans = lazyRetry(() => import("./pages/Plans"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const Disputes = lazyRetry(() => import("./pages/Disputes"));
const DisputeDetail = lazyRetry(() => import("./pages/DisputeDetail"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Help = lazyRetry(() => import("./pages/Help"));
const TechnicianOnboarding = lazyRetry(() => import("./pages/TechnicianOnboarding"));
const PMOnboarding = lazyRetry(() => import("./pages/PMOnboarding"));
const PMDashboard = lazyRetry(() => import("./pages/pm/Dashboard"));
const PMProperties = lazyRetry(() => import("./pages/pm/Properties"));
const PMPropertyDetail = lazyRetry(() => import("./pages/pm/PropertyDetail"));
const PMJobs = lazyRetry(() => import("./pages/pm/Jobs"));
const PMBook = lazyRetry(() => import("./pages/pm/PMBook"));
const PMReports = lazyRetry(() => import("./pages/pm/Reports"));
const PMBilling = lazyRetry(() => import("./pages/pm/Billing"));
const PMContractors = lazyRetry(() => import("./pages/pm/Contractors"));
const PMDisputes = lazyRetry(() => import("./pages/pm/Disputes"));
const PMSettings = lazyRetry(() => import("./pages/pm/Settings"));
const TechDashboard = lazyRetry(() => import("./pages/tech/Dashboard"));
const TechJobComplete = lazyRetry(() => import("./pages/tech/JobComplete"));
const TechPayouts = lazyRetry(() => import("./pages/tech/Payouts"));
const TechHistory = lazyRetry(() => import("./pages/tech/History"));
const TechDisputes = lazyRetry(() => import("./pages/tech/Disputes"));
const AdminDashboard = lazyRetry(() => import("./pages/admin/Dashboard"));
const AdminTechnicians = lazyRetry(() => import("./pages/admin/Technicians"));
const AdminUsers = lazyRetry(() => import("./pages/admin/Users"));
const AdminBookings = lazyRetry(() => import("./pages/admin/Bookings"));
const AdminDisputes = lazyRetry(() => import("./pages/admin/Disputes"));
const AdminSettings = lazyRetry(() => import("./pages/admin/Settings"));
const AdminReconciliation = lazyRetry(() => import("./pages/admin/Reconciliation"));
const AdminReviews = lazyRetry(() => import("./pages/admin/Reviews"));
const Support = lazyRetry(() => import("./pages/Support"));
const Install = lazyRetry(() => import("./pages/Install"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const ServiceLanding = lazyRetry(() => import("./pages/ServiceLanding"));
const ServiceCityLanding = lazyRetry(() => import("./pages/ServiceCityLanding"));
const PricingGuide = lazyRetry(() => import("./pages/PricingGuide"));
const PricingGuideService = lazyRetry(() => import("./pages/PricingGuideService"));
const JobDetail = lazyRetry(() => import("./pages/JobDetail"));
const ProjectsList = lazyRetry(() => import("./pages/projects/ProjectsList"));
import { ProjectsChrome } from "./components/projects/ProjectsChrome";
const NewProject = lazyRetry(() => import("./pages/projects/NewProject"));
const ProjectDetail = lazyRetry(() => import("./pages/projects/ProjectDetail"));
const CompareQuotes = lazyRetry(() => import("./pages/projects/CompareQuotes"));
const TechProjects = lazyRetry(() => import("./pages/tech/Projects"));
const TechSubmitQuote = lazyRetry(() => import("./pages/tech/SubmitQuote"));

const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background animate-in fade-in duration-300 delay-200 fill-mode-backwards">
    <div className="w-full max-w-md space-y-4 px-6">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <div className="flex gap-3">
        <Skeleton className="h-10 flex-1 rounded-lg" />
        <Skeleton className="h-10 flex-1 rounded-lg" />
      </div>
    </div>
  </div>
);
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: globalQueryErrorHandler,
  }),
  mutationCache: new MutationCache({
    onError: globalQueryErrorHandler,
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry permission or auth errors
        if (isPermissionError(error) || isAuthError(error)) return false;
        return failureCount < 1;
      },
      throwOnError: false, // Never throw to ErrorBoundary
    },
    mutations: {
      throwOnError: false,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
            <AuthProvider>
              <OrganizationProvider>
              <TooltipProvider>
              <Toaster />
              <Sonner />
              <SupportChatbot />
               <NetworkStatus />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
              <ScrollToTop />
              <CookieConsent />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/install" element={<Install />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/help" element={<Help />} />
                <Route path="/pricing-guide" element={<PricingGuide />} />
                <Route path="/pricing-guide/:serviceId" element={<PricingGuideService />} />
                <Route path="/services/:serviceId" element={<ServiceLanding />} />
                <Route path="/services/:serviceId/in/:city" element={<ServiceCityLanding />} />
                <Route path="/services/:serviceId/:jobSlug" element={<JobDetail />} />
                <Route path="/start" element={<StartJob />} />
                
                <Route path="/become-technician" element={<TechnicianOnboarding />} />
                {FEATURES.propertyManager && (
                  <Route path="/property-manager" element={<PMOnboarding />} />
                )}
                
                {/* Protected User Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute allowedRoles={["user", "admin"]}>
                    <FeatureErrorBoundary featureName="Dashboard">
                      <Dashboard />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/book" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="Booking">
                      <Book />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/tracking" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="Tracking">
                      <Tracking />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/tracking/:id" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="Tracking">
                      <Tracking />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/history" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="History">
                      <History />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/referrals" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="Referrals">
                      <Referrals />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <FeatureErrorBoundary featureName="Settings">
                      <Settings />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/disputes" element={
                  <ProtectedRoute>
                    <Disputes />
                  </ProtectedRoute>
                } />
                <Route path="/disputes/:id" element={
                  <ProtectedRoute>
                    <DisputeDetail />
                  </ProtectedRoute>
                } />
                <Route path="/support" element={
                  <ProtectedRoute>
                    <Support />
                  </ProtectedRoute>
                } />
                
                {/* Project Routes */}
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <ProjectsChrome><ProjectsList /></ProjectsChrome>
                  </ProtectedRoute>
                } />
                <Route path="/projects/new" element={
                  <ProtectedRoute>
                    <ProjectsChrome><NewProject /></ProjectsChrome>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id" element={
                  <ProtectedRoute>
                    <ProjectsChrome><ProjectDetail /></ProjectsChrome>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:id/quotes" element={
                  <ProtectedRoute>
                    <ProjectsChrome><CompareQuotes /></ProjectsChrome>
                  </ProtectedRoute>
                } />

                {/* Property Manager Routes — hidden in v1, gated by FEATURES.propertyManager */}
                {FEATURES.propertyManager && (<>
                <Route path="/pm/dashboard" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <FeatureErrorBoundary featureName="PM Dashboard">
                      <PMDashboard />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/pm/properties" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMProperties />
                  </ProtectedRoute>
                } />
                <Route path="/pm/properties/:id" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMPropertyDetail />
                  </ProtectedRoute>
                } />
                <Route path="/pm/jobs" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMJobs />
                  </ProtectedRoute>
                } />
                <Route path="/pm/book" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMBook />
                  </ProtectedRoute>
                } />
                <Route path="/pm/reports" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMReports />
                  </ProtectedRoute>
                } />
                <Route path="/pm/billing" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMBilling />
                  </ProtectedRoute>
                } />
                <Route path="/pm/contractors" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMContractors />
                  </ProtectedRoute>
                } />
                <Route path="/pm/disputes" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMDisputes />
                  </ProtectedRoute>
                } />
                <Route path="/pm/settings" element={
                  <ProtectedRoute allowedRoles={["property_manager", "admin"]}>
                    <PMSettings />
                  </ProtectedRoute>
                } />
                </>)}

                {/* Technician Routes */}
                <Route path="/tech" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <FeatureErrorBoundary featureName="Technician Dashboard">
                      <TechDashboard />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/tech/job/:id" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechJobComplete />
                  </ProtectedRoute>
                } />
                <Route path="/tech/payouts" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechPayouts />
                  </ProtectedRoute>
                } />
                <Route path="/tech/history" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechHistory />
                  </ProtectedRoute>
                } />
                <Route path="/tech/disputes" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechDisputes />
                  </ProtectedRoute>
                } />
                <Route path="/tech/dashboard" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/tech/projects" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechProjects />
                  </ProtectedRoute>
                } />
                <Route path="/tech/projects/:id/quote" element={
                  <ProtectedRoute allowedRoles={["technician", "admin"]}>
                    <TechSubmitQuote />
                  </ProtectedRoute>
                } />
                
                {/* Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <FeatureErrorBoundary featureName="Admin Dashboard">
                      <AdminDashboard />
                    </FeatureErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/admin/technicians" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminTechnicians />
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminUsers />
                  </ProtectedRoute>
                } />
                <Route path="/admin/bookings" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminBookings />
                  </ProtectedRoute>
                } />
                <Route path="/admin/disputes" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDisputes />
                  </ProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminSettings />
                  </ProtectedRoute>
                } />
                <Route path="/admin/reconciliation" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="/admin/reviews" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminReviews />
                  </ProtectedRoute>
                } />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </BrowserRouter>
            </TooltipProvider>
            </OrganizationProvider>
          </AuthProvider>
        </LanguageProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
</ErrorBoundary>
);

export default App;
